import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical } from 'lucide-react';

export interface ActionMenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  onSelect: () => void | Promise<void>;
  disabled?: boolean;
  tone?: 'default' | 'danger';
}

interface MenuPosition {
  left: number;
  top: number;
}

const MENU_WIDTH = 176;
const VIEWPORT_PADDING = 8;
const MENU_GAP = 4;

export function ActionMenu({
  items,
  label = 'Actions',
  disabled = false,
}: {
  items: ActionMenuItem[];
  label?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<MenuPosition | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const estimatedHeight = items.length * 36 + 8;
    const menuHeight = menuRef.current?.offsetHeight || estimatedHeight;
    const roomBelow = window.innerHeight - rect.bottom - VIEWPORT_PADDING;
    const openAbove = roomBelow < menuHeight + MENU_GAP && rect.top > menuHeight + MENU_GAP;
    const maxLeft = Math.max(VIEWPORT_PADDING, window.innerWidth - MENU_WIDTH - VIEWPORT_PADDING);

    setPosition({
      left: Math.min(maxLeft, Math.max(VIEWPORT_PADDING, rect.right - MENU_WIDTH)),
      top: openAbove
        ? Math.max(VIEWPORT_PADDING, rect.top - menuHeight - MENU_GAP)
        : rect.bottom + MENU_GAP,
    });
  }, [items.length]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;

    function handlePointerDown(event: MouseEvent) {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    }

    function handleKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key !== 'Escape') return;
      setOpen(false);
      triggerRef.current?.focus();
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !position) return;
    menuRef.current?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus();
  }, [open, position]);

  function openMenu() {
    updatePosition();
    setOpen(true);
  }

  function selectItem(item: ActionMenuItem) {
    if (item.disabled) return;
    setOpen(false);
    void item.onSelect();
  }

  function handleMenuKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const buttons = Array.from(
      menuRef.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? [],
    );
    if (!buttons.length) return;
    const currentIndex = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const nextIndex = event.key === 'Home'
      ? 0
      : event.key === 'End'
        ? buttons.length - 1
        : event.key === 'ArrowUp'
          ? (currentIndex <= 0 ? buttons.length - 1 : currentIndex - 1)
          : (currentIndex + 1) % buttons.length;
    buttons[nextIndex].focus();
  }

  const menu = open && position && typeof document !== 'undefined'
    ? createPortal(
      <div
        ref={menuRef}
        role="menu"
        aria-label={label}
        className="fixed z-[100] w-44 overflow-hidden rounded-md border border-gray-200 bg-white py-1 shadow-lg"
        style={{ left: position.left, top: position.top }}
        onKeyDown={handleMenuKeyDown}
      >
        {items.map((item) => {
          const danger = item.tone === 'danger';
          return (
            <button
              key={item.id}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm disabled:cursor-not-allowed disabled:opacity-50 ${
                danger
                  ? 'text-red-600 hover:bg-red-50'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
              onClick={() => selectItem(item)}
            >
              <span aria-hidden="true" className="flex h-4 w-4 shrink-0 items-center justify-center">{item.icon}</span>
              <span>{item.label}</span>
            </button>
          );
        })}
      </div>,
      document.body,
    )
    : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        title={label}
        disabled={disabled || items.length === 0}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
        onClick={() => open ? setOpen(false) : openMenu()}
        onKeyDown={(event) => {
          if (event.key !== 'ArrowDown') return;
          event.preventDefault();
          openMenu();
        }}
      >
        <MoreVertical className="h-4 w-4" />
      </button>
      {menu}
    </>
  );
}
